import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import ReCAPTCHA from 'react-google-recaptcha';
import isEmpty from 'lodash/isEmpty';

import { FormDataType, useFormState } from 'contexts/FormProvider';
import { FormTextFieldProvider } from 'contexts/FormTextFieldContext';
import { GOOGLE_RECAPTCHA_SITE_KEY } from 'config';

import { Header, StepProgress, CallUs } from 'components';
import Loading from 'components/Loading/Loading';
import AlertBanner from 'components/AlertBanner/AlertBanner';
import InternalErrorMessage from 'components/ErrorHandler/InternalErrorMessage';

import ProviderFooter from 'pages/common/components/Footer';
import { CustomerType } from 'pages/Complaint/InsuredDetails/enum';

import { FormStep, FormStepContentProps } from 'types';
import { useStep } from 'hooks/useStep';
import useHttpClient, { getHeader } from 'hooks/useHttpClient';
import { setFormCompleteEvent, setFormStartEvent, trackAxiosResponse, useTrackPageView } from 'hooks/useAnalytics';

import { PROVIDER_NAMES, FIN_PROVIDER_NAMES } from 'utils/constants';
import { remap } from 'utils/remap';
import { remapBRH } from 'utils/remap_breaches';

import { formatDate, isObjectContainsXss } from 'utils/utils';

import { useContentContext } from '@qbe/dfc-content';
import { PageLayout, qbeTheme as theme } from '@qbe/ads-core';
import { complaintSteps, breachSteps, financialSteps } from './lodgementSteps';
import { useContentComplaintPartners } from 'utils/content';

const Provider = () => {
  const { formData, updateForm, isFNH, isBreach, isComplaint, subjectPrefix, defaultErrorMessage } = useFormState();
  const { getTextContent, getJSXContent } = useContentComplaintPartners({ context: useContentContext() });

  // obtain the url-param for deep-linking
  const deeplink = (new URLSearchParams(window.location.search).get('deeplink') || '').toLowerCase();

  React.useEffect(() => {
    if (deeplink === 'financial hardship') updateForm({ lodgement: 'fha' });
    if (deeplink === 'breach') updateForm({ lodgement: 'breach' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showErrorAlertBox = React.useCallback(
    (show = true) => {
      if (show) {
        setIsError(true);
        setErrorMsg(defaultErrorMessage);
      } else {
        setIsError(false);
      }
    },
    [defaultErrorMessage]
  );

  // TODO: need to move in FormProvider
  const getSteps = (): FormStep[] => {
    switch (formData?.lodgement) {
      case 'fha':
        return financialSteps;
      case 'breach':
        return breachSteps;
      default:
        return complaintSteps;
    }
  };

  const getStepName = (): { label: string; id: string }[] => {
    if (isFNH && currentStep !== 0) {
      return financialSteps.map(({ name, analyticsName }, index) => ({
        label: name,
        id: analyticsName,
        completed: index < currentStep,
      }));
    }

    if (isBreach && currentStep !== 0) {
      return breachSteps.map(({ name, analyticsName }, index) => ({
        label: name,
        id: analyticsName,
        completed: index < currentStep,
      }));
    }

    return complaintSteps.map(({ name, analyticsName }, index) => ({
      label: name,
      id: analyticsName,
      completed: index < currentStep,
    }));
  };

  const steps: FormStep[] = getSteps();

  const { currentStep, nextStep, prevStep } = useStep(
    ['financial hardship', 'breach'].includes(deeplink) ? 1 : 0,
    steps.length
  );

  const lengthIndicator = (value: number) => currentStep === steps.length - value;

  // Non-Financial steps should be rendered in step 1
  const stepsName = getStepName();

  // to modify analytics
  const stepsAnalytics = steps.map(({ analyticsName }) => analyticsName);

  const showComplaintInfo = isComplaint && currentStep !== 0;
  const showFHAInfo = isFNH && currentStep !== 0;

  // Steps
  const renderStepHeader = () => (
    <StepProgress activeStep={currentStep} steps={stepsName} isFinHardship={showFHAInfo} />
  );

  const [isError, setIsError] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

  const hasSelectedPolicy = Boolean(
    isFNH && formData?.policyOrClaim === 'policy' && steps[currentStep].name === PROVIDER_NAMES.STEP2
  );

  const policyTitle = hasSelectedPolicy ? getTextContent('fha.your.details.policyError.title') : null;
  const policyError = hasSelectedPolicy ? getJSXContent('fha.your.details.policyError.alert') : null;

  const isSubmissionStep =
    (isFNH && steps[currentStep].name === FIN_PROVIDER_NAMES.STEP6) ||
    (isComplaint && steps[currentStep].name === PROVIDER_NAMES.STEP4) ||
    (isBreach && steps[currentStep].name === PROVIDER_NAMES.STEP4);

  const url = `/x-digital-remediation-qbe-anzo/api/v1/cms/${isBreach ? 'breach' : 'complaint'}-forms`;

  const [{ loading, error: apiError }, postData] = useHttpClient(
    {
      url,
    },
    { manual: true }
  );

  useTrackPageView(currentStep, stepsAnalytics, formData);

  React.useEffect(() => {
    if (hasSelectedPolicy) setIsError(true);
    if (!hasSelectedPolicy) setIsError(false);
  }, [hasSelectedPolicy]);

  React.useEffect(() => {
    setIsError(false);

    const error = apiError ? apiError?.response?.data?.errors?.[0] : null;

    if (!isEmpty(apiError) && isEmpty(error)) {
      showErrorAlertBox();
    }
    if (!isEmpty(error)) {
      showErrorAlertBox();
      trackAxiosResponse(apiError?.response);

      if (error.code === 'ATTACHMENT_OVERSIZE') {
        setErrorMsg(error.message);
      }
      if (error.code === 'ATTACHMENT_INVALID_FILETYPE') {
        setErrorMsg('This file type is not allowed, please attach a different document type');
      }
    }
  }, [apiError, showErrorAlertBox]);

  const getLastName = (obj: FormDataType) => {
    if (isFNH) return obj.insuredLastName; // no customerLastName/contactLastName in FNH
    if (isBreach) return obj.lastName; // no customerLastName/contactLastName in Breaches

    return obj.insuredIndOrBus === CustomerType.INDIVIDUAL ? obj.customerLastName : obj.contactLastName;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recaptchaRef = React.useRef<any>(null); // react-google-recaptcha doesn't support typescript

  const next = async (newData: Partial<FormDataType>) => {
    setFormCompleteEvent();
    const newFormData = { ...formData, ...newData };

    updateForm(newData);

    if (steps[currentStep].name === PROVIDER_NAMES.STEP2) {
      if (newData.policyOrClaim === 'claim') {
        updateForm({
          policyNumber: '',
        });
      }

      if (newData.policyOrClaim === 'policy') {
        updateForm({
          claimNumber: '',
        });
      }

      if (
        isComplaint &&
        (newData.relationship === 'Collection Agent' ||
          newData.relationship === 'Investigator' ||
          newData.relationship === 'Loss Adjuster' ||
          newData.relationship === 'Loss Assessor')
      ) {
        updateForm({
          contactType: null,
          contactPreferedMethod: null,
          isManualAddressEnabled: false,
          postalAddressObject: null,
          postalAddress: null,
          country: '',
          streetAddress: '',
          suburb: '',
          state: '',
          postcode: '',
        });
      }

      if (newData.contactPreferedMethod !== 'post') {
        updateForm({
          postalAddress: null,
          postalAddressObject: null,
          isManualAddressEnabled: false,
          country: '',
          streetAddress: '',
          suburb: '',
          state: '',
          postcode: '',
        });
      }

      if (newData.contactType === 'no') {
        updateForm({
          contactPreferedMethod: null,
          postalAddress: null,
          postalAddressObject: null,
          country: '',
          streetAddress: '',
          suburb: '',
          state: '',
          postcode: '',
        });
      }
    }

    // Submission Step
    if (isSubmissionStep) {
      if (isObjectContainsXss(newFormData)) {
        showErrorAlertBox();
        return;
      }

      const { complaintDate, files } = newFormData;

      const contents = {
        subject: {
          type: subjectPrefix,
          doc: formatDate(complaintDate ?? new Date()),
          lastName: getLastName(newFormData),
        },
        body: remap(newFormData),
      };

      const contentData = new FormData();
      contentData.append('contents', JSON.stringify(contents));

      if (files) {
        files.forEach((file: File) => {
          contentData.append('attachments', file);
        });
      }
      try {
        setTimeout(() => window.scrollTo(0, 0), 0);
        if (recaptchaRef.current) await recaptchaRef.current.reset();

        const recaptchaToken = await recaptchaRef?.current?.executeAsync();

        await postData({
          data: isBreach ? remapBRH(newFormData) : contentData,
          params: { submitBy: 'provider', channel: 'email' },
          headers: {
            ...getHeader(),
            'x-recaptcha-token': recaptchaToken,
            'Content-Type': isBreach ? 'application/json' : 'multipart/form-data',
          },
        });
      } catch (errors) {
        return;
      }
    }
    nextStep();
  };

  const handleClose = () => setIsError(false);

  const handleStart = () => {
    setFormStartEvent();
  };

  const renderStepContent = () => {
    const StepContent = steps[currentStep].component as React.ComponentClass<FormStepContentProps>;
    return (
      <ErrorBoundary FallbackComponent={InternalErrorMessage}>
        <StepContent defaultValues={formData} onStart={handleStart} onComplete={next} showError={showErrorAlertBox} />
      </ErrorBoundary>
    );
  };

  return (
    <>
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={GOOGLE_RECAPTCHA_SITE_KEY}
        onErrored={showErrorAlertBox}
      />
      <PageLayout
        header={
          <Header
            content={renderStepHeader()}
            onBack={prevStep}
            isBackEnabled={currentStep > 0 && !lengthIndicator(1)}
          />
        }
        footer={<ProviderFooter />}
        background={theme.palette.common.white}
      >
        <Loading show={loading} />
        {currentStep !== 0 && <CallUs isFNH={isFNH} />}
        <FormTextFieldProvider>
          <AlertBanner
            show={(isError && lengthIndicator(2)) || (isError && hasSelectedPolicy)}
            errorMsg={errorMsg || policyError}
            onClose={handleClose}
            title={policyTitle}
          />
          {renderStepContent()}
        </FormTextFieldProvider>
      </PageLayout>
    </>
  );
};

export default Provider;
